import { handleApiResponse } from '@src/api/apiClient/axiosClient.ts';
import { companyEventApiClient } from '@src/api/company/event/eventApiClient';
import { eventMenuApiClient } from '@src/api/company/menu/eventMenuApiClient';
import { Event } from '@src/api/types/event';
import EventContent from '@src/features/shared/EventContent';
import { useScrollMobile } from '@src/hooks/useScrollMobile';
import { COMPANY_URL, ShowProps } from '@src/routes/company';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { generatePath, useParams } from 'react-router-dom';

function EventsShow({ setFeature, setDescription }: ShowProps) {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<Event | null>(null);
  const [eventMenu, setEventMenu] = useState<{ link: string; title: string; isCurrent: boolean }[]>(
    [],
  );

  useEffect(() => {
    if (!id) {
      return;
    }

    window.scrollTo(0, 0);

    const fetchData = async () => {
      const res = await companyEventApiClient.show(Number(id));
      handleApiResponse(res, (data) => {
        setEvent(data);
        setFeature(data.title);
        setDescription?.(data.description);
      });

      const eventMenu = await eventMenuApiClient.index();
      handleApiResponse(eventMenu, (data) => {
        const pathName = location.pathname + location.search;
        const formattedEventMenu = [
          {
            link: COMPANY_URL.EVENTS,
            title: 'すべてのイベント',
            isCurrent:
              pathName === COMPANY_URL.EVENTS || pathName.includes(`${COMPANY_URL.EVENTS}/`),
          },
          ...data.map((item) => ({
            title: item.title,
            link: item.link,
            isCurrent: pathName === item.link,
          })),
        ];
        setEventMenu(formattedEventMenu);
      });
    };

    fetchData();
  }, [id]);

  const { renderScrollMobile } = useScrollMobile({
    items: eventMenu.map((item) => ({
      to: item.link || '',
      label: item.title,
      isCurrent: item.isCurrent,
    })),
  });

  const shareData = event
    ? {
        url: `${window.location.origin}${generatePath(COMPANY_URL.EVENTS_SHOW, { id: event.id })}`,
        title: event.title,
        tags: event.media_tags ? event.media_tags.map((tag) => tag.name) : [],
        disabled: false,
      }
    : null;

  return (
    <main className='pb-16 pt-10 md:pt-4'>
      <Helmet>
        <meta
          name='description'
          content={event?.description}
        />
        <meta
          property='og:title'
          content={event?.title}
        />
        <meta
          property='og:description'
          content={event?.description}
        />
        {event?.thumbnail_url && (
          <meta
            property='og:image'
            content={event.thumbnail_url}
          />
        )}
      </Helmet>
      <div className='m-auto max-w-[740px] lg:px-6'>
        <div className='hidden lg:-order-1 lg:block'>
          <div className='ml-[-24px] w-[calc(100%+48px)] pl-[24px]'>{renderScrollMobile()}</div>
        </div>
        {event && (
          <EventContent
            event={event}
            shareData={shareData}
          />
        )}
      </div>
    </main>
  );
}

export default EventsShow;
